/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Tag, Typography2, useTheme } from '@coursera/cds-core';

import { SKILL_LEVEL_SEGMENTS, getLevelDescription, getLevelName } from 'bundles/program-skills-objectives/utils';
import { ProgressTrackerMyScorePopoverWithScore } from 'bundles/skills-common/private/progress-tracker/ProgressTrackerMyScorePopover';
import { generateSkillsPageUrl } from 'bundles/skills-common/private/utils/skillLinkUtils';

import _t from 'i18n!nls/skills-common';

import ProgressTracker from '../progress-tracker/ProgressTracker';

type Props = {
  skill: {
    skillId: string;
    skillName: string;
    score: number;
    targetScore?: number;
  };
  programSlug?: string;
  showSkillPageLink?: boolean;
};

const useStyles = () => {
  const { palette, spacing } = useTheme();
  return {
    container: css({
      border: `1px solid ${palette.gray[400]}`,
      padding: spacing(16),
    }),
    inCardText: css({
      display: 'flex',
      flexDirection: 'column',
      marginTop: spacing(4),
    }),
  };
};

const SkillAdvancementCard: React.FC<Props> = ({ skill, programSlug, showSkillPageLink }) => {
  const styles = useStyles();

  return (
    <div css={styles.container}>
      <ProgressTracker
        skillName={skill.skillName}
        target={skill.targetScore}
        score={skill.score}
        forScoring
        segments={SKILL_LEVEL_SEGMENTS}
        afterPersonalScore={<Tag>{getLevelName(skill.score)}</Tag>}
        scoreTooltip={
          <ProgressTrackerMyScorePopoverWithScore
            score={skill.score}
            skillId={skill.skillId}
            skillName={skill.skillName}
          />
        }
      >
        <div css={styles.inCardText}>
          {showSkillPageLink && programSlug && (
            <Typography2 component="p" variant="bodySecondary">
              <FormattedMessage
                message={_t('You’re making great progress in the {skill}')}
                skill={
                  <Link variant="standard" href={generateSkillsPageUrl(skill.skillId, programSlug)} target="_blank">
                    {_t('#{skillName} Skill', {
                      skillName: skill.skillName,
                    })}
                  </Link>
                }
              />
            </Typography2>
          )}
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {getLevelDescription(skill.score)}
          </Typography2>
        </div>
      </ProgressTracker>
    </div>
  );
};

export default SkillAdvancementCard;
